import type { ApplicationState, ChecklistItem } from "../../types/tracked-job";
import { isApplySource, isChecklistItem } from "../../types/tracked-job";
import { sanitizeMailInterviewDates, sanitizePlusTracker } from "../plus-myjobs/sanitize-tracker";
import { sanitizeHiredMoment } from "../plus-myjobs/hired-moment";

/**
 * Minimal job fields stored in DynamoDB user-state (matches prior `buildJobPayload` in useUserJobStateSync).
 */
export type JobLikeForUserState = {
  id: string;
  title?: unknown;
  normalisedTitle?: unknown;
  companyId?: unknown;
  companyName?: unknown;
  externalUrl?: string;
  applicationState?: ApplicationState;
  isHidden?: boolean;
  applicationDeadline?: unknown;
  notes?: unknown;
  tags?: unknown;
  checklist?: unknown;
  applySource?: unknown;
  nudgeSnoozedUntil?: unknown;
  plusTracker?: unknown;
  interviewDates?: unknown;
  hiredMoment?: unknown;
  interviewKitAt?: unknown;
};

/**
 * JSON-serializable map for PUT /api/user-state/* job keys. Keeps payloads small and avoids
 * sending entire Algolia hits (nested highlights, huge descriptions, etc.).
 */
export function buildJobUserStatePayload(
  job: JobLikeForUserState,
  extras: Record<string, unknown>
): Record<string, unknown> {
  return {
    id: job.id,
    objectID: job.id,
    title: job.title ?? null,
    normalisedTitle: job.normalisedTitle ?? null,
    companyId: job.companyId ?? null,
    companyName:
      typeof job.companyName === "string" && job.companyName.trim() ? job.companyName : null,
    ...(typeof job.externalUrl === "string" && job.externalUrl.length > 0
      ? { externalUrl: job.externalUrl }
      : {}),
    ...(job.applicationState !== undefined ? { applicationState: job.applicationState } : {}),
    ...(job.isHidden !== undefined ? { isHidden: job.isHidden } : {}),
    ...(typeof job.applicationDeadline === "string" && job.applicationDeadline.length > 0
      ? { applicationDeadline: job.applicationDeadline }
      : {}),
    ...(typeof job.notes === "string" ? { notes: job.notes } : {}),
    ...(Array.isArray(job.tags)
      ? { tags: job.tags.filter((t): t is string => typeof t === "string") }
      : {}),
    ...(Array.isArray(job.checklist)
      ? { checklist: job.checklist.filter((item): item is ChecklistItem => isChecklistItem(item)) }
      : {}),
    // Carried through later rewrites (status moves, notes) so the TEC-722 source isn't lost.
    ...(isApplySource(job.applySource) ? { applySource: job.applySource } : {}),
    // Carried through later rewrites so a "Still waiting" snooze (TEC-723) isn't lost.
    ...(typeof job.nudgeSnoozedUntil === "string" && job.nudgeSnoozedUntil.length > 0
      ? { nudgeSnoozedUntil: job.nudgeSnoozedUntil }
      : {}),
    // Plus tracker extras (TEC-744): one additive object, carried through every later rewrite.
    ...(sanitizePlusTracker(job.plusTracker) ? { plusTracker: sanitizePlusTracker(job.plusTracker) } : {}),
    // Interview dates the Applying area's mail ingest wrote (server-owned): a client rewrite is a full-blob replace, so carry them or they would be lost.
    ...(sanitizeMailInterviewDates(job.interviewDates) ? { interviewDates: sanitizeMailInterviewDates(job.interviewDates) } : {}),
    // The Hired moment's answer, carried through later rewrites so it is asked once per hire.
    ...(sanitizeHiredMoment(job.hiredMoment) ? { hiredMoment: sanitizeHiredMoment(job.hiredMoment) } : {}),
    // Set when Scout built an interview kit for the job, carried so the card keeps linking the kit.
    ...(typeof job.interviewKitAt === "string" && job.interviewKitAt.length > 0 ? { interviewKitAt: job.interviewKitAt } : {}),
    ...extras,
  };
}

export function buildSavedJobUserStatePayload(
  job: { id?: string; objectID?: string } & Record<string, unknown>,
  savedAt: string
): Record<string, unknown> {
  const id = String(job.id ?? job.objectID ?? "");
  return buildJobUserStatePayload(
    { ...job, id } as JobLikeForUserState,
    { savedAt }
  );
}
